import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push, replace } from 'connected-react-router';
import moment from 'moment-timezone';
import { debounce, isEqual } from 'lodash';

import Strings from '../../Strings';
import { EndedEnrollmentShippingWarning } from '../../components/EnrollmentShippingWarning';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import { notificationActions } from '../../components/Notification/redux/actions';
import { openModalAction } from '../../actions/modal';
import { GET_DELIVERY_FOLLOWUPS_RESULT, GET_REVIEW_QUEUE_RESULT, shippingActions } from './actions';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../constants';
import { TextInput } from '../../components/PageHeader/TextInput';
import Select from '../../components/Select';
import { GET_MY_ORGS_RESULT, actions as orgFilterActions } from '../../components/OrgSelect/orgFilterActions';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition, OrgFilter } from '../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { hasPermission, PERMISSIONS } from '../../utils/userPermissions';
import { isShipper, isSuperUser } from '../../utils/userRoles';
import { formatDevices, OrderStatusEnum } from './ShippingOrders';
import { PageHeader } from '../../components/PageHeader';
import SimpleModal from '../../containers/Modal/SimpleModal';
import { TextArea } from '../../containers/Form';
import { Tabs, Tab } from '../../components/Tabs';
import { toolTipForTable } from '../../utils';
import { actions } from '../SuperUser/Patients/redux/actions';
import {
  getSearchParamFromUrl,
  hydrateTableRequestFromUrl,
  saveSearchParamToUrl,
  useGlobalPageSize,
} from '../../utils/tableUtils';
import {
  initialReviewQueueRequest,
  lastExistingPage,
  withOffset,
  withOrgFilters,
  withPageSize,
  withSearch,
} from './reviewQueueRequest';

export function RejectOrderModal({ onCancel, onConfirm }) {
  const [reason, setReason] = useState('');

  return (
    <SimpleModal
      name="reject-order-modal"
      title={Strings.shipping.rejectOrder}
      onCancel={onCancel}
      onSubmit={() => onConfirm(reason)}
      isSubmitDisabled={!reason}
      content={(
        <TextArea
          label={Strings.shipping.rejectOrderReason}
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={4}
          highlightInvalid
          isRequired
        />
      )}
    />
  );
}

RejectOrderModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export function RecentDeliveries({ getDeliveryFollowups, getPatientInfo, openConfirmModal, onNavigate, refreshTimestamp }) {
  const [loading, setLoading] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [followup, setFollowup] = useState('pending');

  const followupOptions = [
    { value: 'pending', label: Strings.pending },
    { value: 'done', label: Strings.done },
  ];

  const loadData = () => {
    setLoading(true);
    getDeliveryFollowups({ followup }).then(resp => {
      if (resp?.type === GET_DELIVERY_FOLLOWUPS_RESULT) {
        setDeliveries((resp?.response?.data || []).map(entry => ({
          ...entry,
          device_models: [...new Set((entry.deliveries || []).flatMap(d => d.device_models || []))],
        })));
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, [getDeliveryFollowups, refreshTimestamp, followup]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredDeliveries(deliveries || []);
      return;
    }

    const query = searchQuery.toLowerCase();
    const propertiesToSearch = ['patient_name', 'patient_uuid', 'pharmacy_patient_id', 'patient_phone'];
    setFilteredDeliveries((deliveries || []).filter(d =>
      propertiesToSearch.some(prop => (d[prop]?.toString()?.toLowerCase() || '').includes(query))
        || (d.device_models || []).some(m => m.toLowerCase().includes(query))));
  }, [deliveries, searchQuery]);

  const onMarkReviewed = delivery => {
    openConfirmModal({
      title: Strings.shipping.markDeliveryReviewedQuestion,
      onConfirmAction: shippingActions.setDeliveryFollowup(delivery.patient_uuid, { acknowledged: true }),
      onSuccessNotification: Strings.shipping.deliveryReviewedSuccessfully,
      caption: Strings.shipping.markDeliveryReviewed,
      hideCaution: true,
      confirmPostAction: () => loadData(),
    });
  };

  const onUnmarkReviewed = delivery => {
    openConfirmModal({
      title: Strings.shipping.unmarkDeliveryReviewedQuestion,
      onConfirmAction: shippingActions.setDeliveryFollowup(delivery.patient_uuid, { acknowledged: false }),
      onSuccessNotification: Strings.shipping.deliveryUnreviewedSuccessfully,
      caption: Strings.shipping.unmarkDeliveryReviewed,
      hideCaution: true,
      confirmPostAction: () => loadData(),
    });
  };

  const onRowClick = async delivery => {
    const patientResp = await getPatientInfo(delivery.patient_uuid);
    if (patientResp?.response?.id) {
      onNavigate(`/cap-patients/${patientResp?.response?.id}`);
    }
  };

  const buttons = React.useMemo(() => (
    followup === 'done'
      ? [{
        icon: 'refresh',
        onClick: (_, delivery) => onUnmarkReviewed(delivery),
        text: Strings.shipping.unmarkDeliveryReviewed,
      }]
      : [{
        icon: 'check',
        onClick: (_, delivery) => onMarkReviewed(delivery),
        text: Strings.done,
      }]
  ), [followup]);

  const columns = React.useMemo(() => [
    columnHelper.accessor('patient_name', {
      header: Strings.patientNameText,
      cell: info => info.getValue() || '-',
      size: 200,
    }),
    columnHelper.accessor('pharmacy_patient_id', {
      header: Strings.capPatient.patientId,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('patient_phone', {
      header: Strings.phoneNo,
      cell: info => info.getValue() || '-',
      size: 160,
    }),
    columnHelper.accessor('device_models', {
      header: Strings.shipping.deviceModels,
      cell: info => (info.getValue()?.length ? toolTipForTable(info.getValue().join(', '), info.getValue()) : '-'),
      size: 300,
      enableSorting: false,
    }),
    columnHelper.accessor('delivered_shipment_count', {
      header: Strings.shipping.deliveriesCount,
      cell: info => info.getValue() ?? '-',
      size: 120,
    }),
    columnHelper.accessor('latest_delivered_at', {
      header: Strings.shipping.latestDelivered,
      cell: info => (info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-'),
      size: 200,
      sortingFn: 'dateSortingFn',
      sortUndefined: -1,
    }),
  ], []);

  return (
    <>
      <PageHeader
        right={(
          <>
            <TextInput placeholder={Strings.search} onChange={e => setSearchQuery(e.target.value)} />
            <Select
              id="delivery-followup-filter"
              data={followupOptions}
              value={followup}
              onChange={opt => setFollowup(opt.value)}
            />
          </>
        )}
      />
      <Table3
        isLoading={loading}
        name="recent-deliveries"
        data={filteredDeliveries || []}
        columns={columns}
        onRowClick={onRowClick}
        buttons={buttons}
        enableColumnFiltering
        noScroll
        pinFirstColumn
      />
    </>
  );
}

RecentDeliveries.propTypes = {
  getDeliveryFollowups: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  getPatientInfo: PropTypes.func.isRequired,
  refreshTimestamp: PropTypes.any,
};


function OrdersForReview(props) {
  const { refreshTimestamp, filters } = props;
  const { getReviewQueue, onNavigate, rejectOrder, openConfirmModal, showNotification, getOrgs, getPatientInfo, refreshData, getDeliveryFollowups } = props;

  const globalPageSize = useGlobalPageSize();
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ offset: 0, limit: globalPageSize, totalRecords: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [searchText, setSearchText] = useState(getSearchParamFromUrl() || '');
  const [orgs, setOrgs] = useState([]);
  const canEdit = hasPermission(PERMISSIONS.SHIPPING_PENDING_REVIEW_ORDERS_EDIT) || isShipper() || isSuperUser();

  const pageRequest = useRef(initialReviewQueueRequest(globalPageSize));
  const requestSequence = useRef(0);

  const loadData = React.useCallback(async () => {
    requestSequence.current += 1;
    const sequence = requestSequence.current;
    setLoading(true);
    try {
      const resp = await getReviewQueue(pageRequest.current);
      if (sequence !== requestSequence.current) return;
      if (resp?.type === GET_REVIEW_QUEUE_RESULT) {
        const entries = resp.response?.entries || [];
        const { offset, limit } = pageRequest.current;
        const lastPage = lastExistingPage(pageRequest.current, resp.response?.pagination?.totalRecords);
        if (!entries.length && lastPage.offset < offset) {
          // Table3 re-syncs its page index only when pagination.offset changes;
          // record the empty page first, or landing back on the offset shown
          // before the jump would leave the pager on the page typed in.
          setPagination(resp.response?.pagination || { offset, limit, totalRecords: 0 });
          pageRequest.current = lastPage;
          loadData();
          return;
        }
        setOrders(entries);
        setPagination(resp.response?.pagination || { offset, limit, totalRecords: entries.length });
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [getReviewQueue]);

  useEffect(() => {
    getOrgs().then(resp => {
      if (resp?.type === GET_MY_ORGS_RESULT) {
        setOrgs(resp?.response?.data || []);
      }
    });
  }, [getOrgs, refreshTimestamp]);

  const hasLoaded = useRef(false);

  useEffect(() => {
    hydrateTableRequestFromUrl(pageRequest.current, globalPageSize);
    pageRequest.current = withSearch(pageRequest.current, getSearchParamFromUrl());
  }, [globalPageSize]);

  // Leaving any page that has an AdvancedFiltersBar resets the filters store to a
  // fresh [] right after this page's first render, so compare the resulting org
  // selection instead of reloading on every new filters reference.
  useEffect(() => {
    const next = withOrgFilters(pageRequest.current, filters);
    if (hasLoaded.current && isEqual(next.orgId, pageRequest.current.orgId)) return;
    hasLoaded.current = true;
    pageRequest.current = next;
    loadData();
  }, [filters, loadData]);

  useEffect(() => {
    if (refreshTimestamp) loadData();
  }, [refreshTimestamp, loadData]);

  const displayedOrders = React.useMemo(() => orders.map(order => ({
    ...order,
    org_name: orgs.find(org => org.masked_id === order.org_uuid)?.name || '',
  })), [orders, orgs]);

  const onSearchQueryChange = React.useMemo(() => debounce(search => {
    const next = withSearch(pageRequest.current, search);
    if (next.search === pageRequest.current.search) return;
    saveSearchParamToUrl(search);
    pageRequest.current = next;
    loadData();
  }, 300), [loadData]);

  useEffect(() => () => onSearchQueryChange.cancel(), [onSearchQueryChange]);

  const onOffsetChange = offset => {
    pageRequest.current = withOffset(pageRequest.current, offset);
    loadData();
  };

  const onPageSizeChange = limit => {
    pageRequest.current = withPageSize(pageRequest.current, limit);
    loadData();
  };

  const onAcceptOrder = order => {
    openConfirmModal({
      title: Strings.formatString(
        Strings.shipping.acceptOrderQuestion,
        order.patient_name,
      ),
      onConfirmAction: shippingActions.acceptOrder(order.order_uuid, {}),
      onSuccessNotification: Strings.shipping.orderAcceptedSuccessfully,
      caption: Strings.shipping.acceptOrder,
      hideCaution: true,
      confirmPostAction: refreshData,
    });
  };

  const onRejectOrder = (order, reason) => {
    rejectOrder(order.order_uuid, reason).then(() => {
      refreshData();
      setSelectedOrder(null);
      setShowRejectModal(false);
      showNotification(Strings.shipping.orderRejectedSuccessfully);
    });
  }

  const buttons = React.useMemo(() => [
    {
      icon: 'check',
      onClick: (_, order) => onAcceptOrder(order),
      text: Strings.accept,
      visible: (_, order) => order.status === OrderStatusEnum.pending_review,
    },
    {
      icon: 'edit',
      onClick: (_, order) => onNavigate(`/shipping/order-details/${order.order_uuid}`),
      text: Strings.edit,
    },
    {
      icon: 'delete',
      onClick: (_, order) => {setSelectedOrder(order); setShowRejectModal(true);},
      text: Strings.reject,
      visible: (_, order) => order.status === OrderStatusEnum.pending_review,
    },
  ]);

  const clickableCell = (value) => <div className="clickable">{value}</div>;

  const columns = React.useMemo(() => [
    columnHelper.accessor('order_uuid', {
      header: Strings.shipping.orderId,
      cell: info => <div className="icon-and-text-container"><div className="icon-and-text-content">{clickableCell(info.getValue())}</div><EndedEnrollmentShippingWarning order={{ ...info.row.original, flow: 'outbound' }} compact /></div>,
      size: 250,
    }),
    columnHelper.accessor('org_name', {
      header: Strings.organization,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('patient_name', {
      header: Strings.patientNameText,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('pharmacy_patient_id', {
      header: Strings.capPatient.patientId,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('order_items', {
      header: Strings.devices,
      cell: info => formatDevices(info.getValue()),
      size: 300,
      enableSorting: false,
    }),
    columnHelper.accessor('created_at', {
      header: Strings.createdAt,
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 150,
    }),
  ], []);

  const getTabRightContent = React.useCallback(() => {
    return (
      <>
        <TextInput
          placeholder={Strings.search}
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
            onSearchQueryChange(e.target.value);
          }}
        />
        <AdvancedFilters>
          {orgs?.length > 0 && <Filter definition={OrgFilter(orgs, false, 'org_uuid', Strings.organization).org_uuid} />}
        </AdvancedFilters>
      </>
    );
  }, [orgs, onSearchQueryChange]);

  const defaultTabIndex = new URLSearchParams(window.location.search).get('tab') === 'recent' ? 1 : 0;

  return (
    <>
      <Tabs defaultIndex={defaultTabIndex}>
        <Tab label={Strings.shipping.orderStatuses.pending_review} key="pending-review">
          <PageHeader
            right={getTabRightContent()}
          />
          <AdvancedFiltersBar
            customFilters={{...OrgFilter(orgs, false, 'org_uuid', Strings.organization)}}
          />
          <Table3
            isLoading={loading}
            name="shipping-orders"
            data={displayedOrders}
            columns={columns}
            onRowClick={(order) => onNavigate(`/shipping/order-details/${order.order_uuid}`)}
            enableColumnFiltering
            buttons={canEdit ? buttons : undefined}
            noScroll
            pinFirstColumn
            pagination={pagination}
            onOffsetChange={onOffsetChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Tab>
        <Tab label={Strings.shipping.recent} key="recent">
          <RecentDeliveries
            getDeliveryFollowups={getDeliveryFollowups}
            getPatientInfo={getPatientInfo}
            openConfirmModal={openConfirmModal}
            onNavigate={onNavigate}
            refreshTimestamp={refreshTimestamp}
          />
        </Tab>
      </Tabs>
      {showRejectModal && (
        <RejectOrderModal
          onCancel={() => {setShowRejectModal(false); setSelectedOrder(null);}}
          onConfirm={(reason) => onRejectOrder(selectedOrder, reason)}
        />
      )}
    </>
  );
}

OrdersForReview.propTypes = {
  deleteOrder: PropTypes.func.isRequired,
  refreshTimestamp: PropTypes.any,
  showNotification: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  filters: PropTypes.array,
  orgIds: PropTypes.string,
  orgs: PropTypes.array,
  orders: PropTypes.array,
  loading: PropTypes.bool,
  onNavigate: PropTypes.func.isRequired,
  replacePath: PropTypes.func.isRequired,
  refreshData: PropTypes.func.isRequired,
  getDeliveryFollowups: PropTypes.func.isRequired,
  getPatientInfo: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  getReviewQueue: params => dispatch(shippingActions.getReviewQueue(params)),
  getDeliveryFollowups: params => dispatch(shippingActions.getDeliveryFollowups(params)),
  rejectOrder: (order, reason) => dispatch(shippingActions.rejectOrder(order, {reason})),
  getOrgs: request => dispatch(orgFilterActions.getMyOrgs(request)),
  getPatientInfo: patientId => dispatch(actions.getPatientInfo(patientId)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  onNavigate: url => dispatch(push(url)),
  replacePath: url => dispatch(replace(url)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrdersForReview);